/**
 * Module dependencies.
 */

import { FaqProps } from 'src/types/faqs';
import { color, media, units } from 'src/styles/utils';
import { fetchFaqs } from 'src/api/app/faqs/fetch-faqs';
import { fetchPage } from 'src/api/app/pages/fetch-page';
import { getApiPageSlugByLocale, useLocalizedRoute } from 'src/core/utils/routes';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp, theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import React, { ReactElement } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import SchemaFaq from 'src/components/schema-faq';
import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography';
import arrowRight from 'src/assets/svg/arrow-right.svg';
import size from 'lodash/size';
import styled, { css } from 'styled-components';
import useFaqs from 'src/api/app/faqs/use-faqs';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';

/**
 * `PageProps` type.
 */

type PageProps = {
  descriptionSeo?: string | null,
  id: string,
  slug: string,
  subtitle?: string | null,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * `Props` type.
 */

type Props = {
  data: PageProps,
  faqs: FaqProps[],
  slug: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding: ${theme('dimensions.navbarHeight')}px 0 ${units(25)};
  `}

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  padding-top: ${units(5)};

  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title title . .'
      '. .     faqs  . .';
    grid-template-columns: 1fr 1fr 9fr 1fr 1fr;
    padding-top: ${units(10)};
  `}

  ${media.min('md')`
    grid-template-columns: 1fr 2fr 9fr 2fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;
  margin-bottom: ${units(5)};

  ${media.min('ms')`
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({
  as: 'span'
})`
  display: block;
  grid-area: subTitle;
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `FaqsList` styled component.
 */

const FaqsList = styled.div`
  border-top: 1px solid ${color('grey100')};
  display: flex;
  flex-direction: column;
  grid-area: faqs;
`;

/**
 * `SvgIcon` styled component.
 */

const SvgIcon = styled(Svg)`
  transform: translateX(0);
  transition: transform 0.2s linear;
`;

/**
 * `FaqItem` styled component.
 */

const FaqItem = styled(RouterLink)`
  align-items: center;
  border-bottom: 1px solid ${color('grey100')};
  color: ${color('textColor')};
  display: grid;
  grid-gap: ${units(2)};
  grid-template-areas: 'faqTitle faqArrow';
  grid-template-columns: 1fr max-content;
  padding: ${units(2.5)} ${units(1.5)} ${units(2.5)} ${units(1)};

  &:focus,
  &:hover {
    ${SvgIcon} {
      transform: translateX(10px);
    }
  }
`;

/**
 * `FaqTitle` styled component.
 */

const FaqTitle = styled(Type.Paragraph)`
  grid-area: faqTitle;
`;

/**
 * `FaqArrow` styled component.
 */

const FaqArrow = styled.span`
  grid-area: faqArrow;
  height: ${units(4)};
`;

/**
 * `EmptyFaq` styled component.
 */

const EmptyFaq = styled(Type.Paragraph)`
  grid-area: faqs;
  padding: ${units(8)} 0;
`;

/**
 * `Faqs` page.
 */

const Faqs = (props: Props): ReactElement => {
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const {
    data: faqs,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs
  } = useFaqs<FaqProps[]>({ initialData: props.faqs });

  const subtitle = pageData?.subtitle;
  const title = pageData?.title;
  const isLoading = isLoadingPage || isLoadingFaqs;
  const isSuccess = isSuccessPage && isSuccessFaqs;

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={pageData?.descriptionSeo}
        hrefLangs={getHrefLangs(pageData?.translations, 'faqs')}
        pageTitle={title}
        title={pageData?.titleSeo}
      />

      <SchemaFaq
        faqs={faqs?.map(faq => ({
          answer: faq.descriptionSeo || '',
          question: faq.name
        })) || []}
      />

      <Section isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <Container>
            <Grid>
              <Title>
                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  {`${title} `}

                  {subtitle && (
                    <SubTitle>
                      {subtitle}
                    </SubTitle>
                  )}
                </ParallaxFadeInUp>
              </Title>

              {size(faqs) > 0 ? (
                <FaqsList>
                  {faqs.map(({ id, name, slug }: FaqProps) => (
                    <FadeInUpAnimation key={id}>
                      <FaqItem
                        href={routeResolver('faq', { slug })}
                        locale={locale}
                      >
                        <FaqTitle>
                          {name}
                        </FaqTitle>

                        <FaqArrow>
                          <SvgIcon
                            icon={arrowRight}
                            size={units(4)}
                          />
                        </FaqArrow>
                      </FaqItem>
                    </FadeInUpAnimation>
                  ))}
                </FaqsList>
              ) : (
                <EmptyFaq>
                  <FadeInUpAnimation>
                    {translate('faqs:list.emptyFaq')}
                  </FadeInUpAnimation>
                </EmptyFaq>
              )}
            </Grid>
          </Container>
        )}
      </Section>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps({ locale, query }) {
  const slug = getApiPageSlugByLocale('faqs', locale);
  const faqs = await fetchFaqs({ locale });
  const pageData = await fetchPage({
    hasPreview: query?.preview,
    locale,
    slug
  });

  if (!pageData) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data: pageData,
      faqs,
      slug
    }
  };
}

/**
 * Export `Faqs` page.
 */

export default Faqs;
